import { useEffect, useMemo, useRef } from 'react';
import type { SetURLSearchParams } from 'react-router-dom';
import { useBackofficePreference } from './backoffice-preferences';

interface UsePersistedSearchParamsOptions {
  scope: string;
  keys: string[];
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

function readManagedValues(searchParams: URLSearchParams, keys: string[]): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const key of keys) {
    const value = searchParams.get(key);
    if (value !== null && value !== '') {
      values[key] = value;
    }
  }
  return values;
}

export function usePersistedSearchParams({
  scope,
  keys,
  searchParams,
  setSearchParams,
}: UsePersistedSearchParamsOptions): void {
  const keySignature = keys.join('\u001f');
  const defaultValue = useMemo(() => ({ params: {} as Record<string, unknown> }), []);
  const { value, setValue, loaded } = useBackofficePreference(scope, {
    defaultValue,
    debounceMs: 800,
  });
  const hydratedRef = useRef(false);
  const currentManagedValues = useMemo(
    () => readManagedValues(searchParams, keys),
    [keySignature, searchParams]
  );
  const currentSignature = JSON.stringify(currentManagedValues);

  useEffect(() => {
    hydratedRef.current = false;
  }, [scope]);

  useEffect(() => {
    if (!loaded || hydratedRef.current) {
      return;
    }

    hydratedRef.current = true;
    const savedParams = value.params;
    if (!savedParams || typeof savedParams !== 'object') {
      return;
    }

    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        let changed = false;
        for (const key of keys) {
          const savedValue = (savedParams as Record<string, unknown>)[key];
          if (next.has(key) || typeof savedValue !== 'string' || savedValue === '') {
            continue;
          }
          next.set(key, savedValue);
          changed = true;
        }
        return changed ? next : current;
      },
      { replace: true }
    );
  }, [keys, loaded, setSearchParams, value.params]);

  useEffect(() => {
    if (!loaded || !hydratedRef.current) {
      return;
    }
    setValue({ params: currentManagedValues });
  }, [currentManagedValues, currentSignature, loaded, setValue]);
}
